import * as THREE from "three";
import cdt2d from "cdt2d";
import { CSS2DObject } from "three/addons/renderers/CSS2DRenderer.js";
import { toRaw } from "vue";
import API from "@/api/API";
import { viewerColors } from "@/utils/viewer-colors";
import { BLACK, RENDERING_ORDER } from "../constants";
import trashRed from "@/assets/model/trash_red.svg";
import { ElMessage } from "element-plus";

export const updateMidPointPosition = function (
  instancedMesh,
  index,
  startPoint,
  endPoint
) {
  const transformationMatrix = new THREE.Matrix4();
  instancedMesh.getMatrixAt(index, transformationMatrix);

  transformationMatrix.decompose(
    new THREE.Vector3(),
    new THREE.Quaternion(),
    new THREE.Vector3()
  );

  const newPosition = new THREE.Vector3().lerpVectors(
    startPoint,
    endPoint,
    0.5
  );

  transformationMatrix.setPosition(newPosition);
  instancedMesh.setMatrixAt(index, transformationMatrix);
  instancedMesh.instanceMatrix.needsUpdate = true;
  instancedMesh.computeBVH();
};

export const createNonReactivePoint = function (
  referencePoint,
  color = viewerColors.MIDPOINT_COLOR
) {
  const dotMaterial = new THREE.MeshBasicMaterial({
    color: color,
  });

  const tempPoint = new THREE.Mesh(this.dotGeometry, dotMaterial);
  tempPoint.position.x = referencePoint.x;
  tempPoint.position.y = referencePoint.y;
  tempPoint.position.z = referencePoint.z;

  return tempPoint;
};

export const createNonReactiveAreaPoint = function (
  referencePoint,
  color = viewerColors.MIDPOINT_COLOR
) {
  const pointGroup = new THREE.Group();
  pointGroup.position.copy(referencePoint);

  const dotMaterial = new THREE.MeshBasicMaterial({
    color: BLACK,
  });

  const wrapper = new THREE.Mesh(this.dotGeometry, dotMaterial);

  const innerDotMaterial = new THREE.MeshBasicMaterial({
    color: color,
  });
  const innerDot = new THREE.Mesh(this.dotGeometry, innerDotMaterial);

  pointGroup.add(wrapper);
  pointGroup.add(innerDot);

  return pointGroup;
};

export const checkForClosedArea = function (area) {
  let pointIntersects = this.raycaster.intersectObject(area.points[0]);

  if (pointIntersects.length > 0 || area.closeArea) return true;
  return false;
};

export const inMeasurementAreaCreationMode = function () {
  if (this.measurementAreas.length === 0) return false;

  const lastArea = this.measurementAreas[this.measurementAreas.length - 1];
  if (lastArea.closed) return false;

  return true;
};

export const detectIntesectionForCursorChange = function (event) {
  if (this.inMeasurementAreaCreationMode()) return;
  if (this.dragOn) return;

  this.setMousePosition(event);

  const measurementAreaObjects = [];
  this.measurementAreas
    .filter((area) => area.closed && (!area.plane || area.plane.visible))
    .forEach((area) => {
      measurementAreaObjects.push(area.plane);
      measurementAreaObjects.push(area.pointInstancedMesh);
    });

  let intersects = this.raycaster.intersectObjects(measurementAreaObjects);

  if (intersects.length === 0) {
    if (this.active === 4) return this.changeCursorToCrosshair();
    if (this.active === 0) return this.restoreDefaultCursor();
  }

  this.changeCursorToPointer();
};

export const detectMeasurementAreaIntersection = function () {
  const measurementAreaObjects = [];
  this.measurementAreas
    .filter((area) => area.closed && (!area.plane || area.plane.visible))
    .forEach((area) => {
      measurementAreaObjects.push(area.plane);
      measurementAreaObjects.push(area.pointInstancedMesh);
    });

  let intersects = this.raycaster.intersectObjects(measurementAreaObjects);
  if (intersects.length === 0) {
    return false;
  }

  let areaToExpand = intersects[0].object;
  let clickedArea = this.measurementAreas.find(
    (area) => area.plane && area.plane.id === areaToExpand.id
  );

  if (!clickedArea) {
    clickedArea = this.measurementAreas.find(
      (area) => area.pointInstancedMesh.id === areaToExpand.id
    );
  }

  return clickedArea.plane;
};

export const showMeasurementAreaTrash = function (object) {
  const area = this.measurementAreas.find(
    (area) => area.plane.uuid === object.uuid
  );
  const label = area.label;
  const secondColumn = label.element.children[1];
  secondColumn.style = "margin: auto; margin-left: 8px;";
};

export const hideMeasurementAreaTrash = function (object) {
  const area = this.measurementAreas.find(
    (area) => area.plane.uuid === object.uuid
  );
  const label = area.label;
  const secondColumn = label.element.children[1];
  secondColumn.style = "margin: auto; margin-left: 8px; display: none;";
};

export const removeDashedLine = function () {
  if (this.dashedMeasurementLine) {
    this.scene.remove(this.dashedMeasurementLine);
    this.dashedMeasurementLine.children.forEach((child) => {
      if (child.geometry) child.geometry.dispose();
      if (child.material) child.material.dispose();
    });
  }
};
export const checkForComplexArea = function (area) {
  const points = area.points;
  const triangleIndices = this.getTriangleIndices(
    points,
    this.getAxisDifferences(points.map((point) => point.position))
  );
  if (!triangleIndices?.length) return true;
  return false;
};

export const selectMeasurementArea = function (event, resetMode = true) {
  event.preventDefault();

  if (this.measurementDrag || this.active === 2) return;

  if (event.target.tagName !== "CANVAS") return;
  this.setMousePosition(event);

  const measurementIntersects = this.detectMeasurementIntersection(event, true);
  if (measurementIntersects?.length > 0) return;

  const measurementAreaPlane = this.detectMeasurementAreaIntersection();

  if (measurementAreaPlane) {
    this.detectedMeasurementArea = measurementAreaPlane;
  } else {
    this.detectedMeasurementArea = null;
  }

  const measurementArea = this.measurementAreas.find(
    (area) => area.plane.id === measurementAreaPlane.id
  );

  this.highlightSelectedArea(measurementArea, resetMode);

  const areaIndex = this.measurementAreas.findIndex((element) => {
    return element.label?.id === measurementArea?.label?.id;
  });

  if (areaIndex >= 0) {
    this.selectMeasurementAreaFromCanvas(areaIndex);
  }
};

export const selectedFromSidebarEvent = function (id, resetMode = true) {
  const measurementArea = this.measurementAreas.find(
    (area) => area.label.id === id
  );
  if (measurementArea) {
    this.highlightSelectedArea(measurementArea, resetMode);
  } else {
    console.log("No measurement area found with that ID");
  }
};

export const highlightSelectedArea = function (
  measurementArea,
  resetMode = true
) {
  const measurementAreaPlane = measurementArea?.plane;

  if (measurementArea && this.active !== 4 && resetMode) {
    this.toggleActive(0);
  }

  if (this.active === 4) this.removeUnfinishedAreaMeasurements();

  if (measurementAreaPlane) {
    this.measurementAreas.forEach((area) => {
      this.unselectMeasurementArea(area);
    });

    this.unselectDistances();

    this.selectedMeasurementArea = measurementArea;
    if (!resetMode) {
      this.detectedMeasurementArea = measurementAreaPlane;
      this.fromDraggStart = true;
    } else {
      this.fromDraggStart = false;
    }
    if (measurementAreaPlane.material.opacity !== 0) {
      measurementAreaPlane.material.opacity = 0.7;
    }
    this.setLinesColorForArea(measurementArea);
    this.setPointsColorForArea(measurementArea);
    this.setMidPointsColorForArea(measurementArea);
    this.showMeasurementAreaTrash(measurementAreaPlane);
  } else {
    this.detectedMeasurementArea = null;
    if (this.selectedMeasurementArea) {
      if (this.selectedMeasurementArea.plane.material.opacity !== 0)
        this.selectedMeasurementArea.plane.material.opacity = 0.5;
      this.hideMeasurementAreaTrash(this.selectedMeasurementArea.plane);
      const area = this.measurementAreas.find(
        (area) => area.plane.id === this.selectedMeasurementArea.plane.id
      );
      this.setLinesColorForArea(area, viewerColors.MIDPOINT_COLOR);
      this.setPointsColorForArea(area, viewerColors.MIDPOINT_COLOR);
      this.setMidPointsColorForArea(area, viewerColors.MIDPOINT_COLOR);
    }
  }
};

export const unselectFromSidebar = function (area) {
  if (area) {
    this.unselectMeasurementArea(area);
    if (area.plane && area.plane.material.opacity !== 0) {
      area.plane.material.opacity = 0.5;
      this.hideMeasurementAreaTrash(area.plane);
    }
  }
};

export const unselectMeasurementArea = function (area) {
  if (area && area.plane) {
    area.plane.material.opacity = 0.5;
    this.setLinesColorForArea(area, viewerColors.MIDPOINT_COLOR);
    this.setPointsColorForArea(area, viewerColors.MIDPOINT_COLOR);
    this.setMidPointsColorForArea(area, viewerColors.MIDPOINT_COLOR);
    this.hideMeasurementAreaTrash(area.plane);
  }
};

export const reAddMeasurementPoint = function () {
  document.removeEventListener("click", this.reAddMeasurementPoint, false);
  document.addEventListener("click", this.addMeasurementPoint, false);
};

export const addMeasurementPoint = function (event) {
  event.preventDefault();

  if (this.disableClick(event)) return;

  this.setMousePosition(event);

  // Check for closed areas first
  if (this.measurementAreas.length > 0) {
    const measurementAreaPlane = this.detectMeasurementAreaIntersection();
    if (
      measurementAreaPlane &&
      this.measurementAreas[this.measurementAreas.length - 1].closed
    ) {
      this.selectMeasurementArea(event, false);
      return;
    }
  }

  let intersects = this.raycaster.intersectObject(this.modelObject.children[0]);
  if (intersects.length < 1) return; // mouse doesn't intersect the model

  if (this.dragOn) return; // disable point placement when drag is on

  // Check for duplicate points before creating ANY objects
  if (
    this.measurementAreas.length > 0 &&
    !this.measurementAreas[this.measurementAreas.length - 1].closed
  ) {
    const currentArea = this.measurementAreas[this.measurementAreas.length - 1];
    let pointIntersects = this.raycaster.intersectObjects(currentArea.points);

    if (pointIntersects.length > 0) {
      // Only proceed if this is the first point AND we have at least 3 points
      const isFirstPoint =
        pointIntersects[0].object.uuid === currentArea.points[0].uuid;
      if (!isFirstPoint || currentArea.points.length < 3) {
        return;
      }
    }
  }

  let newFixedLine = null;
  let midPoint = null;
  let oldTempLine = null;
  let oldTempLabel = null;
  let oldFirstPoint = null;
  let lastArea = null;

  // replace dashed line with solid line
  if (
    this.measurementAreas.length > 0 &&
    !this.measurementAreas[this.measurementAreas.length - 1].closed
  ) {
    lastArea = this.measurementAreas[this.measurementAreas.length - 1];
    oldFirstPoint = lastArea.firstPoint;

    if (lastArea.tempLine) {
      oldTempLine = lastArea.tempLine;
      oldTempLabel = lastArea.tempLabel;
      const solidLinePoints = [
        lastArea.firstPoint.position,
        this.checkForClosedArea(lastArea)
          ? lastArea.points[0].position
          : lastArea.tempPoint.position,
      ];
      let solidLine = this.createReactiveThickLine(solidLinePoints, 4.0);

      this.removeDashedLine();

      this.scene.add(solidLine);

      midPoint = this.createReactiveMidPoint(
        lastArea.firstPoint,
        this.checkForClosedArea(lastArea)
          ? lastArea.points[0]
          : lastArea.tempPoint
      );

      lastArea.tempLine = null;
      if (lastArea.lines) {
        newFixedLine = {
          line: solidLine,
          firstPoint: lastArea.firstPoint,
          secondPoint: this.checkForClosedArea(lastArea)
            ? lastArea.points[0]
            : lastArea.tempPoint,
          label: lastArea.tempLabel,
          midPoint: midPoint,
        };
        lastArea.lines.push(newFixedLine);
      } else {
        newFixedLine = {
          line: solidLine,
          firstPoint: lastArea.firstPoint,
          secondPoint: this.checkForClosedArea(lastArea)
            ? lastArea.points[0]
            : lastArea.tempPoint,
          label: lastArea.tempLabel,
          midPoint: midPoint,
        };
        lastArea.lines = [newFixedLine];
      }
    }
  }

  // check for closed area
  if (
    this.measurementAreas.length > 0 &&
    this.measurementAreas[this.measurementAreas.length - 1]?.points.length >
      2 &&
    !this.measurementAreas[this.measurementAreas.length - 1].closed
  ) {
    if (
      this.checkForClosedArea(
        this.measurementAreas[this.measurementAreas.length - 1]
      )
    ) {
      this.measurementAreas[this.measurementAreas.length - 1].complex =
        this.checkForComplexArea(
          this.measurementAreas[this.measurementAreas.length - 1]
        );
      this.drawMeasurementAreaPlane(
        this.measurementAreas[this.measurementAreas.length - 1]?.points,
        true
      );

      return;
    }
  }

  // check for closed areas
  if (this.measurementAreas.length > 0) {
    const measurementAreaPlane = this.detectMeasurementAreaIntersection();
    if (
      measurementAreaPlane &&
      this.measurementAreas[this.measurementAreas.length - 1].closed
    ) {
      this.selectMeasurementArea(event, false);
      return;
    }
  }

  if (this.selectedMeasurementArea) {
    this.deselectSidebarArea();
    this.unselectMeasurementArea(this.selectedMeasurementArea);
  }

  let o = intersects[0];
  let pIntersect = o.point.clone();
  this.scene.worldToLocal(pIntersect);

  const dot = this.createReactivePoint(
    pIntersect,
    viewerColors.POINT_COLOR,
    this.isFirstMeasurementPoint
  );

  const tempPoint = this.createNonReactivePoint(pIntersect);

  if (
    this.measurementAreas.length > 0 &&
    !this.measurementAreas[this.measurementAreas.length - 1].closed
  ) {
    this.measurementAreas[this.measurementAreas.length - 1].points.push(dot);
  } else {
    this.measurementAreas.push({
      points: [dot],
      closed: false,
    });
  }

  let measurementArea = this.measurementAreas[this.measurementAreas.length - 1];

  measurementArea.firstPoint = dot;
  measurementArea.tempPoint = tempPoint;

  if (
    measurementArea.lines &&
    measurementArea.lines[measurementArea.lines.length - 1].firstPoint
  ) {
    measurementArea.lines[measurementArea.lines.length - 1].secondPoint = dot;
  }
  this.camera.lookAt(measurementArea.firstPoint.position);

  const firstPoint = new THREE.Vector3();
  const secondPoint = new THREE.Vector3();
  measurementArea.firstPoint.getWorldPosition(firstPoint);
  measurementArea.tempPoint.getWorldPosition(secondPoint);
  let points = [firstPoint, secondPoint];

  let newLine = this.createReactiveThickLine(points, 4.0, true);
  this.dashedMeasurementLine = newLine;
  measurementArea.tempLine = newLine;

  let newLabel = this.createLabelBetweenTwoPoints(
    firstPoint,
    secondPoint,
    0.5,
    false,
    null
  );
  measurementArea.tempLabel = newLabel;

  this.scene.add(dot);
  const area = this.measurementAreas[this.measurementAreas.length - 1];
  const label =
    lastArea && lastArea.lines.length > 0 ? lastArea.tempLabel : null;
  const line = newFixedLine ? newFixedLine.line : null;

  if (area.points.length > 1)
    // add point undo callback function
    this.undoStack.push({
      action: "ADD_POINT",
      area: area,
      point: dot,
      line: line,
      midPoint: midPoint,
      oldTempLine: oldTempLine,
      oldFirstPoint: oldFirstPoint,
      newTempLine: newLine,
      oldTempLabel: oldTempLabel,
      newTempLabel: newLabel,
      fixedLine: newFixedLine,
    });
  this.resetRedoStack();
};

export const stickMousePointerToDotForMeasurementArea = function (
  currentMeasurementArea,
  intersectionPoint
) {
  const firstDot = currentMeasurementArea.points[0];
  const lastDot =
    currentMeasurementArea?.points[currentMeasurementArea.points.length - 1];
  const numPoints = currentMeasurementArea.points.length;

  const distance = firstDot.position.distanceTo(intersectionPoint);
  const threshold = 0.25;

  if (firstDot && numPoints > 2) {
    if (distance <= threshold) {
      this.setAreaAsClosed(
        firstDot,
        currentMeasurementArea,
        false,
        viewerColors.SELECTED_POINT_COLOR
      );

      const firstPoint = new THREE.Vector3();
      const secondPoint = new THREE.Vector3();
      firstDot.getWorldPosition(firstPoint);
      lastDot.getWorldPosition(secondPoint);
      let points = [firstPoint, secondPoint];
      this.updateLinePosition(this.dashedMeasurementLine, points);

      const label = currentMeasurementArea.tempLabel;
      this.updateLabelBetweenTwoPoints(
        label,
        firstPoint,
        secondPoint,
        0.5,
        true
      );
    } else {
      this.setAreaAsOpen(currentMeasurementArea, viewerColors.POINT_COLOR);
    }
  }
};

export const updatePreliminaryPointPositionForMeasurementArea = function (
  event
) {
  if (this.dragOn) return;

  if (this.shouldThrottle()) return;

  if (
    this.measurementAreas.length > 0 &&
    !this.measurementAreas[this.measurementAreas.length - 1].closed
  ) {
    this.setMousePosition(event);

    const mouseData = { x: this.mouse.x, y: this.mouse.y };
    const cameraData = {
      matrixWorld: this.camera.matrixWorld.toArray(),
      projectionMatrix: this.camera.projectionMatrix.toArray(),
      isOrthographic: this.isOrthographicCamera,
    };

    if (this.pendingLineRaycastId) {
      this.lineWorker.postMessage({
        type: "CANCEL_REQUEST",
        requestId: this.pendingLineRaycastId,
      });
    }

    this.pendingLineRaycastId = Date.now();

    this.lineWorker.postMessage({
      type: "RAYCAST_REQUEST",
      requestId: this.pendingLineRaycastId,
      data: {
        mouse: mouseData,
        cameraData,
      },
    });
  }
};

export const handleMeasurementAreaLineWorkerResults = function (
  intersectionPoint,
  requestId
) {
  if (requestId !== this.pendingLineRaycastId) {
    return;
  }

  this.pendingLineRaycastId = null;

  if (!intersectionPoint) return;

  let pIntersect = new THREE.Vector3(
    intersectionPoint.x,
    intersectionPoint.y,
    intersectionPoint.z
  );

  this.scene.worldToLocal(pIntersect);
  const measurementArea =
    this.measurementAreas[this.measurementAreas.length - 1];

  if (measurementArea.closed) return;

  let marker = measurementArea.tempPoint;
  marker.position.x = pIntersect.x;
  marker.position.y = pIntersect.y;
  marker.position.z = pIntersect.z;

  const firstPoint = new THREE.Vector3();
  const secondPoint = new THREE.Vector3();
  measurementArea.firstPoint.getWorldPosition(firstPoint);
  measurementArea.tempPoint.getWorldPosition(secondPoint);
  let points = [firstPoint, secondPoint];

  if (this.dashedMeasurementLine) {
    this.updateLinePosition(this.dashedMeasurementLine, points);
  }

  const line = this.dashedMeasurementLine;
  const lineObject = this.scene.getObjectById(line.id);
  if (!lineObject) this.scene.add(toRaw(line));

  const label = measurementArea.tempLabel;
  const distance = this.updateLabelBetweenTwoPoints(
    label,
    firstPoint,
    secondPoint,
    0.5,
    true
  );

  const object = this.scene.getObjectById(label.id);
  if (!object && distance > 0.5) {
    this.scene.add(toRaw(label));
    label.addedToScene = true;
  }

  const numPoints = measurementArea.points.length;
  if (numPoints > 2) {
    const measurementAreaFirstDot = new THREE.Vector3();
    measurementArea.points[0].getWorldPosition(measurementAreaFirstDot);
    const endingPoints = [measurementAreaFirstDot, secondPoint];

    if (this.areaEndingLine) {
      this.updateLinePosition(this.areaEndingLine, endingPoints);
    } else {
      let newDottedLine = this.createReactiveThickLine(
        endingPoints,
        4.0,
        true,
        true
      );
      this.areaEndingLine = newDottedLine;
      this.scene.add(newDottedLine);
    }
  }

  this.stickMousePointerToDotForMeasurementArea(
    measurementArea,
    intersectionPoint
  );
};

export const checkLabelsOverlapWithMidpoints = function (area) {
  for (let line of area.lines) {
    const overlaps = this.checkLabelOverlapWithPoint(
      line.label,
      line.midPoint.children[0]
    );
  }
};

export const drawMeasurementAreaPlane = async function (
  points,
  createArea = true
) {
  const currentMeasurementArea =
    this.measurementAreas[this.measurementAreas.length - 1];

  this.resetUndoStack();
  this.resetRedoStack();
  this.removeSnapIcon(true);

  const instancedMesh = this.replacePointsWithInstancedMesh(points);
  this.scene.add(instancedMesh);

  const midpointInstancedMesh = this.replacePointsWithInstancedMesh(
    currentMeasurementArea.lines.map((line) => line.midPoint),
    viewerColors.POINT_COLOR,
    false,
    true
  );
  this.scene.add(midpointInstancedMesh);

  const vectorPoints = points.map(
    (point) =>
      new THREE.Vector3(point.position.x, point.position.y, point.position.z)
  );

  const pointsAsArray = vectorPoints.map((point) => [
    point.x,
    point.y,
    point.z,
  ]);

  const flatPoints = [].concat(...pointsAsArray);

  const geometry = new THREE.BufferGeometry();
  const vertices = new Float32Array(flatPoints);

  let indices;

  if (currentMeasurementArea.complex) {
    indices = this.generateIndices(vectorPoints);
  } else {
    const triangleIndices = this.getTriangleIndices(
      currentMeasurementArea.points,
      this.getAxisDifferences(
        currentMeasurementArea.points.map((point) => point.position)
      )
    );
    indices = triangleIndices
      ? [].concat(...triangleIndices)
      : this.generateIndices(vectorPoints);
  }

  geometry.setAttribute("position", new THREE.BufferAttribute(vertices, 3));
  geometry.setIndex(new THREE.Uint16BufferAttribute(indices, 1));

  const material = new THREE.MeshBasicMaterial({
    color: viewerColors.POINT_COLOR,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: currentMeasurementArea.complex ? 0.0 : 0.5,
  });

  this.removeDashedLine();

  if (this.areaEndingLine) this.removeObjectFromScene(this.areaEndingLine);

  this.areaEndingLine = null;

  // on slow cpus if the points are added quickly, the labels are not added to the scene
  this.verifyMeasurementAreaLabelsVisibility(currentMeasurementArea);

  this.combineAreaLines(currentMeasurementArea);

  const plane = new THREE.Mesh(geometry, material);
  plane.material.depthTest = false;
  plane.renderOrder = RENDERING_ORDER.MEASUREMENT_PLANE;

  let surfaceArea = "";
  let weightedAngle = "";

  if (!currentMeasurementArea.complex) {
    ({ surfaceArea, weightedAngle } = this.calculateAreaMeasurement(points));
  }

  const areaParagraph = document.createElement("p");
  areaParagraph.textContent = isNaN(surfaceArea)
    ? `${surfaceArea}m²`
    : `${this.$n(Number(surfaceArea), "decimal")}m²`;

  const angleParagraph = document.createElement("p");
  angleParagraph.textContent = isNaN(weightedAngle)
    ? `${weightedAngle}°`
    : `${this.$n(Number(weightedAngle), "decimal")}°`;

  const trashCan = document.createElement("img");

  trashCan.src = trashRed;
  trashCan.style = "cursor:pointer; width: 25px; pointer-events: all;";

  trashCan.addEventListener("click", this.removeSelectedMeasurementArea);

  const trashCanDiv = document.createElement("div");
  trashCanDiv.append(trashCan);

  const div = document.createElement("div");
  div.className = "areaLabel row";

  const firstColumn = document.createElement("div");
  const secondColumn = document.createElement("div");

  firstColumn.className = "column";
  firstColumn.append(areaParagraph);
  firstColumn.append(angleParagraph);

  secondColumn.className = "column";
  secondColumn.style = "margin: auto; margin-left: 8px; display: none;";
  secondColumn.append(trashCanDiv);

  div.appendChild(firstColumn);
  div.appendChild(secondColumn);

  let label = new CSS2DObject(div);
  const centerPoint = this.getCenterPointFromVectors(vectorPoints);
  label.position.set(centerPoint.x, centerPoint.y, centerPoint.z);

  label.layers.set(0);
  label.renderOrder = RENDERING_ORDER.MEASUREMENT_AREA_LABEL;
  this.scene.add(label);

  this.measurementAreas[this.measurementAreas.length - 1].plane = plane;
  this.measurementAreas[this.measurementAreas.length - 1].points =
    vectorPoints.map((point) => {
      return {
        position: point,
      };
    });
  this.measurementAreas[this.measurementAreas.length - 1].expanded = false;
  this.measurementAreas[this.measurementAreas.length - 1].panelSpacing = null;
  this.measurementAreas[
    this.measurementAreas.length - 1
  ].transparencyLevel = 100;
  this.measurementAreas[this.measurementAreas.length - 1].panelType = null;
  this.measurementAreas[this.measurementAreas.length - 1].panels = [];
  this.measurementAreas[this.measurementAreas.length - 1].closed = true;
  this.measurementAreas[
    this.measurementAreas.length - 1
  ].closemeasurementArea = false;
  this.measurementAreas[this.measurementAreas.length - 1].label = label;
  this.measurementAreas[this.measurementAreas.length - 1].indices = indices;
  this.measurementAreas[this.measurementAreas.length - 1].surfaceArea =
    surfaceArea;
  this.measurementAreas[this.measurementAreas.length - 1].angle = weightedAngle;
  this.measurementAreas[this.measurementAreas.length - 1].pointInstancedMesh =
    instancedMesh;
  this.measurementAreas[
    this.measurementAreas.length - 1
  ].midpointInstancedMesh = midpointInstancedMesh;
  this.scene.add(plane);

  const areaPoints = points.map((point) => {
    return {
      x: point.position.x,
      y: point.position.y,
      z: point.position.z,
    };
  });

  if (!this.anonymousUser && createArea) {
    try {
      const { data } = await this.createMeasurementAreaObject(areaPoints);
      this.measurementAreas[this.measurementAreas.length - 1].id = data;
      this.areaMeasurementActivate();
      this.highlightSelectedArea(
        this.measurementAreas[this.measurementAreas.length - 1],
        true
      );
    } catch (e) {
      console.log(e);
    }
  }
};

export const getTriangleIndices = function (points, trial = 0) {
  const delPoints = points.map((v) => {
    return [
      trial < 2 ? v.position.x : v.position.y,
      trial < 1 ? v.position.y : v.position.z,
    ];
  });
  let delEdges = [];

  for (var i = 0; i < points.length; i++) {
    const pair = [i, (i + 1) % points.length];
    delEdges.push(pair);
  }
  try {
    const triangleIndices = cdt2d(delPoints, delEdges, { exterior: false });
    return triangleIndices;
  } catch (e) {
    if (trial < 2) {
      return this.getTriangleIndices(points, trial + 1);
    }
    console.warn("cdt2d triangulation failed on all axes, treating as complex area", e);
    return null;
  }
};

export const getAxisDifferences = function (vectors) {
  const minX = Math.min(...vectors.map((vector) => vector.x));
  const maxX = Math.max(...vectors.map((vector) => vector.x));
  const diffX = maxX - minX;

  const minY = Math.min(...vectors.map((vector) => vector.y));
  const maxY = Math.max(...vectors.map((vector) => vector.y));
  const diffY = maxY - minY;

  const minZ = Math.min(...vectors.map((vector) => vector.z));
  const maxZ = Math.max(...vectors.map((vector) => vector.z));
  const diffZ = maxZ - minZ;

  if (diffX === Math.min(diffX, diffY, diffZ)) {
    return 2;
  } else if (diffY === Math.min(diffX, diffY, diffZ)) {
    return 1;
  } else {
    return 0;
  }
};

export const calculateAreaMeasurement = function (points) {
  let triangleIndices = this.getTriangleIndices(
    points,
    this.getAxisDifferences(points.map((point) => point.position))
  );
  if (!triangleIndices) {
    return { surfaceArea: "", weightedAngle: "" };
  }
  let triangles = [];
  for (let i = 0; i < triangleIndices.length; i++) {
    triangles.push([]);
    for (let j = 0; j < triangleIndices[i].length; j++) {
      triangles[i].push(points[triangleIndices[i][j]]);
    }
  }
  let area = 0;
  let angle = 0;
  for (let i = 0; i < triangles.length; i++) {
    const vertexA = triangles[i][0];
    const vertexB = triangles[i][1];
    const vertexC = triangles[i][2];

    const triangleArea = this.calculateTriangleArea(
      vertexA.position,
      vertexB.position,
      vertexC.position
    );
    const triangleAngle = this.calculateTriangleAngle(
      vertexA.position,
      vertexB.position,
      vertexC.position
    );
    area = Number(area) + Number(triangleArea);
    angle = Number(angle) + Number(triangleAngle) * Number(triangleArea);
  }
  return {
    surfaceArea: area.toFixed(2),
    weightedAngle: (angle / area).toFixed(2),
  };
};

export const createTriangleMesh = function (vertexA, vertexB, vertexC) {
  var geometry = new THREE.BufferGeometry();

  // Create arrays to hold the vertex positions
  var vertices = new Float32Array([
    vertexA.position.x,
    vertexA.position.y,
    vertexA.position.z,
    vertexB.position.x,
    vertexB.position.y,
    vertexB.position.z,
    vertexC.position.x,
    vertexC.position.y,
    vertexC.position.z,
  ]);

  // Create a face using indices
  var indices = new Uint32Array([0, 1, 2]);

  // Add the vertices and indices to the BufferGeometry
  geometry.setAttribute("position", new THREE.BufferAttribute(vertices, 3));
  geometry.setIndex(new THREE.BufferAttribute(indices, 1));

  var color = new THREE.Color(Math.random(), Math.random(), Math.random());

  const fillMaterial = new THREE.MeshBasicMaterial({
    color,
    side: THREE.DoubleSide,
    depthTest: false,
  });
  const wireMaterial = new THREE.MeshBasicMaterial({
    color,
    side: THREE.DoubleSide,
    wireframe: true,
    depthTest: false,
  });

  const fillMesh = new THREE.Mesh(geometry, fillMaterial);
  const wireMesh = new THREE.Mesh(geometry, wireMaterial);
  fillMesh.renderOrder = 999;
  wireMesh.renderOrder = 1000;

  const group = new THREE.Group();
  group.add(fillMesh);
  group.add(wireMesh);
  return group;
};

export const calculateTriangleAngle = function (vertexA, vertexB, vertexC) {
  const vector1 = new THREE.Vector3().subVectors(vertexB, vertexA);
  const vector2 = new THREE.Vector3().subVectors(vertexC, vertexA);

  const normal = new THREE.Vector3().crossVectors(vector1, vector2).normalize();

  const verticalVector = new THREE.Vector3(0, -1, 0);

  const angle = verticalVector.angleTo(normal);

  let angleDegrees = THREE.MathUtils.radToDeg(angle);
  if (angleDegrees > 90) angleDegrees = 180 - angleDegrees;

  return angleDegrees.toFixed(2);
};

export const removeSelectedMeasurementArea = function () {
  let areaIndex, area;
  for (let i = 0; i < this.measurementAreas.length; i++) {
    let areaObj = this.measurementAreas[i];
    if (areaObj.plane.uuid === this.selectedMeasurementArea.plane.uuid) {
      area = areaObj;
      areaIndex = i;
    }
  }

  const areaPositions = area.points.map((point) => {
    return {
      x: point.position.x,
      y: point.position.y,
      z: point.position.z,
    };
  });

  this.removeObjectFromScene(area.plane);
  this.removeObjectFromScene(area.label);

  for (let point of area.points) {
    this.removeObjectFromScene(point);
  }

  for (let line of area.lines) {
    this.removeObjectFromScene(line.label);
    this.removeObjectFromScene(line.midPoint);
  }

  this.removeObjectFromScene(area.combinedLine);

  this.removeObjectFromScene(area.pointInstancedMesh);
  this.removeObjectFromScene(area.midpointInstancedMesh);

  this.measurementAreas.splice(areaIndex, 1);
  this.deleteMeasurementAreaObject(area.id);

  this.undoStack.push({
    action: "DELETE_MEASUREMENT_AREA",
    area: {
      position: areaPositions,
      name: area.name,
    },
  });
  this.resetRedoStack();

  this.selectedMeasurementArea = null;
};

export const hideAllMeasurementAreas = function () {
  this.measurementAreas.forEach((area) => {
    hideSingleMeasurementArea(area);
  });
};

export const showAllMeasurementAreas = function () {
  this.measurementAreas.forEach((area) => {
    if (area.show) {
      showSingleMeasurementArea(area);
    }
  });
};
export const hideSingleMeasurementArea = function (area) {
  // Hide the main plane
  if (area.plane) {
    area.plane.visible = false;
  }
  // Hide the label
  if (area.label) {
    area.label.visible = false;
  }
  if (area.pointInstancedMesh) {
    area.pointInstancedMesh.visible = false;
  }
  if (area.combinedLine) {
    area.combinedLine.visible = false;
  }
  if (area.midpointInstancedMesh) {
    area.midpointInstancedMesh.visible = false;
  }
  // Show all lines and their labels
  area.lines.forEach((line) => {
    if (line.label) {
      line.label.visible = false;
    }
  });
};
export const showSingleMeasurementArea = function (area) {
  if (area.plane) {
    area.plane.visible = true;
  }
  // Hide the label
  if (area.label) {
    area.label.visible = true;
  }
  if (area.pointInstancedMesh) {
    area.pointInstancedMesh.visible = true;
  }
  if (area.combinedLine) {
    area.combinedLine.visible = true;
  }
  if (area.midpointInstancedMesh) {
    area.midpointInstancedMesh.visible = true;
  }
  // Show all lines and their labels
  area.lines.forEach((line) => {
    if (line.label) {
      line.label.visible = true;
    }
  });
};

export const enableMeasurementPointDragMode = function () {
  document.addEventListener("mousedown", this.dragMeasurementAreaPointStart);
};

export const disableMeasurementPointDragMode = function () {
  document.removeEventListener("mousedown", this.dragMeasurementAreaPointStart);
  document.removeEventListener("mousemove", this.dragMeasurementAreaPoint);
  document.removeEventListener("mouseup", this.dragMeasurementAreaPointEnd);
};

export const holdMeasurementArea = function () {
  this.cleanNavigationSetup();
  this.disableDefaultNavigation();
  if (this.active === 0)
    document.removeEventListener("click", this.selectMeasurementArea, false);
  if (this.active === 4)
    document.removeEventListener("click", this.addMeasurementPoint, false);
};

export const unholdMeasurementArea = function () {
  this.restoreDefaultNavigation();

  setTimeout(() => {
    if (this.active === 0)
      document.addEventListener("click", this.selectMeasurementArea, false);
    if (this.active === 4)
      document.addEventListener("click", this.addMeasurementPoint, false);

    this.highlightSelectedArea(this.draggedAreaPoint.area, false);
    this.draggedAreaPoint = null;
  }, 0);
};

export const dragMeasurementAreaPointStart = function (e) {
  if (this.disableClick(e)) return;

  this.setMousePosition(e);

  if (
    this.measurementAreas.length === 0 ||
    !this.measurementAreas[this.measurementAreas.length - 1].closed
  )
    return;

  const closedMeasurementAreas = this.measurementAreas.filter(
    (area) => area.closed
  );
  const points = closedMeasurementAreas.map((area) => area.pointInstancedMesh);
  const midpoints = closedMeasurementAreas.map(
    (area) => area.midpointInstancedMesh
  );

  const pointIntersects = this.raycaster.intersectObjects(points, true);

  const midpointIntersects = this.raycaster.intersectObjects(midpoints, true);

  if (pointIntersects.length === 0 && midpointIntersects.length === 0) return;

  if (this.isMeasurementSelected) {
    this.unselectDistances();
    this.measurementDrag = false;
  }

  let isMidpoint = false;
  let intersectionPoint;

  if (pointIntersects.length > 0) {
    intersectionPoint = pointIntersects[0];
  } else {
    intersectionPoint = midpointIntersects[0];
    isMidpoint = true;
  }

  this.draggedAreaPoint = {
    area: this.measurementAreas.find((area) =>
      isMidpoint
        ? area.midpointInstancedMesh.id === intersectionPoint.object.id
        : area.pointInstancedMesh.id === intersectionPoint.object.id
    ),
    index: intersectionPoint.instanceId,
    isMidpoint,
  };

  const originalPosition = new THREE.Vector3();
  const transformationMatrix = new THREE.Matrix4();

  if (isMidpoint) {
    this.draggedAreaPoint.area.midpointInstancedMesh.getMatrixAt(
      this.draggedAreaPoint.index,
      transformationMatrix
    );
    this.draggedAreaPoint.area.midPointAdded = false;
  } else {
    this.draggedAreaPoint.area.pointInstancedMesh.getMatrixAt(
      this.draggedAreaPoint.index,
      transformationMatrix
    );
  }

  transformationMatrix.decompose(
    originalPosition,
    new THREE.Quaternion(),
    new THREE.Vector3()
  );

  this.draggedAreaPoint.lastPosition = {
    x: originalPosition.x,
    y: originalPosition.y,
    z: originalPosition.z,
  };

  this.draggedAreaPoint.originalPosition = {
    x: originalPosition.x,
    y: originalPosition.y,
    z: originalPosition.z,
  };

  this.holdMeasurementArea();

  this.unselectMeasurementArea(this.selectedMeasurementArea);
  this.highlightSelectedArea(this.draggedAreaPoint.area, false);

  document.addEventListener("mousemove", this.dragMeasurementAreaPoint);
  document.addEventListener("mouseup", this.dragMeasurementAreaPointEnd);
  this.disableDragControls();
  this.dragOn = true;
  this.changeCursorToPointer();
};

export const dragMeasurementAreaPoint = function (e) {
  if (this.shouldThrottle()) return;

  if (!this.dragOn) return;

  if (
    this.draggedAreaPoint.isMidpoint &&
    !this.draggedAreaPoint.area.midPointAdded
  ) {
    return this.dragMeasurementAreaMidPoint(e);
  }

  this.setMousePosition(e);

  const mouseData = { x: this.mouse.x, y: this.mouse.y };
  const cameraData = {
    matrixWorld: this.camera.matrixWorld.toArray(),
    projectionMatrix: this.camera.projectionMatrix.toArray(),
    isOrthographic: this.isOrthographicCamera,
  };

  if (this.pendingLineRaycastId) {
    this.lineWorker.postMessage({
      type: "CANCEL_REQUEST",
      requestId: this.pendingLineRaycastId,
    });
  }

  this.pendingLineRaycastId = Date.now();

  this.lineWorker.postMessage({
    type: "RAYCAST_REQUEST",
    requestId: this.pendingLineRaycastId,
    data: {
      mouse: mouseData,
      cameraData,
    },
    draggedMarkerId: true,
    isMidpoint: this.draggedAreaPoint.isMidpoint,
  });
};

export const handleDragMeasurementAreaLineWorkerResults = function (
  intersectionPoint,
  requestId
) {
  if (requestId !== this.pendingLineRaycastId) {
    return;
  }

  this.pendingLineRaycastId = null;

  if (!intersectionPoint || !this.draggedAreaPoint) return;

  let pIntersect = new THREE.Vector3(
    intersectionPoint.x,
    intersectionPoint.y,
    intersectionPoint.z
  );

  this.scene.worldToLocal(pIntersect);

  const area = this.draggedAreaPoint.area;
  const instancedMesh = area.pointInstancedMesh;
  const pointIndex = this.draggedAreaPoint.index;

  const transformationMatrix = new THREE.Matrix4();
  const newPosition = new THREE.Vector3();

  instancedMesh.getMatrixAt(pointIndex, transformationMatrix);

  transformationMatrix.decompose(
    newPosition,
    new THREE.Quaternion(),
    new THREE.Vector3()
  );

  newPosition.set(pIntersect.x, pIntersect.y, pIntersect.z);

  transformationMatrix.setPosition(newPosition);
  instancedMesh.setMatrixAt(pointIndex, transformationMatrix);

  instancedMesh.instanceMatrix.needsUpdate = true;
  instancedMesh.computeBVH();
  area.midpointInstancedMesh.computeBVH();

  area.points[pointIndex].position = newPosition;

  const pointsMerged = this.checkMergePoints(pointIndex, area);
  this.reDrawMeasurementAreaFromPoint(pointsMerged);

  this.draggedAreaPoint.pointsMerged = pointsMerged;

  if (pointsMerged) {
    this.dragOn = false;

    if (this.active === 4)
      setTimeout(() => {
        this.reAddMeasurementPoint();
      }, 500);
    else
      setTimeout(() => {
        this.toggleActive(0);
      }, 500);
  }
};

export const dragMeasurementAreaMidPoint = function (e) {
  this.setMousePosition(e);

  const mouseData = { x: this.mouse.x, y: this.mouse.y };
  const cameraData = {
    matrixWorld: this.camera.matrixWorld.toArray(),
    projectionMatrix: this.camera.projectionMatrix.toArray(),
    isOrthographic: this.isOrthographicCamera,
  };

  if (this.pendingLineRaycastId) {
    this.lineWorker.postMessage({
      type: "CANCEL_REQUEST",
      requestId: this.pendingLineRaycastId,
    });
  }

  this.pendingLineRaycastId = Date.now();

  this.lineWorker.postMessage({
    type: "RAYCAST_REQUEST",
    requestId: this.pendingLineRaycastId,
    data: {
      mouse: mouseData,
      cameraData,
    },
    draggedMarkerId: true,
    isMidpoint: this.draggedAreaPoint.isMidpoint,
  });
};

export const handleDragMidPointMeasurementAreaLineWorkerResults = function (
  intersectionPoint,
  requestId
) {
  if (requestId !== this.pendingLineRaycastId) {
    return;
  }
  const area = this.draggedAreaPoint.area;

  if (area.midPointAdded)
    return this.handleDragMeasurementAreaLineWorkerResults(
      intersectionPoint,
      requestId
    );

  this.pendingLineRaycastId = null;

  if (!intersectionPoint) return;

  let pIntersect = new THREE.Vector3(
    intersectionPoint.x,
    intersectionPoint.y,
    intersectionPoint.z
  );

  this.scene.worldToLocal(pIntersect);

  const instancedMesh = area.midpointInstancedMesh;
  const pointIndex = this.draggedAreaPoint.index;

  const transformationMatrix = new THREE.Matrix4();
  const newPosition = new THREE.Vector3();

  instancedMesh.getMatrixAt(pointIndex, transformationMatrix);

  transformationMatrix.decompose(
    newPosition,
    new THREE.Quaternion(),
    new THREE.Vector3()
  );

  newPosition.set(pIntersect.x, pIntersect.y, pIntersect.z);

  transformationMatrix.setPosition(newPosition);
  instancedMesh.setMatrixAt(pointIndex, transformationMatrix);

  instancedMesh.instanceMatrix.needsUpdate = true;
  instancedMesh.computeBVH();
  area.pointInstancedMesh.computeBVH();

  if (area.lines[pointIndex].midPoint.children.length > 0) {
    this.updatePositionOnObject(
      area.lines[pointIndex].midPoint.children[0],
      newPosition
    );
    this.updatePositionOnObject(
      area.lines[pointIndex].midPoint.children[1],
      newPosition
    );
  } else {
    this.updatePositionOnObject(area.lines[pointIndex].midPoint, newPosition);
  }

  this.draggedAreaPoint.intersectionPoint = pIntersect;

  const line = area.lines[pointIndex];

  this.hideObjectFromScene(line.label);

  area.points.splice(pointIndex + 1, 0, {
    position: pIntersect,
  });

  area.midPointAdded = true;

  // replace points with instanced mesh
  const pointInstancedMesh = this.replacePointsWithInstancedMesh(
    area.points,
    viewerColors.SELECTED_LINE_COLOR,
    area.pointInstancedMesh
  );
  this.scene.add(pointInstancedMesh);
  area.pointInstancedMesh = pointInstancedMesh;

  // replace midpoints with instanced mesh
  for (let line of area.lines) {
    this.removeObjectFromScene(line.midPoint);
    this.removeObjectFromScene(line.label);
  }
  this.reCreateLinesFromPoints(area);

  const midpointInstancedMesh = this.replacePointsWithInstancedMesh(
    area.lines.map((line) => line.midPoint),
    viewerColors.SELECTED_LINE_COLOR,
    area.midpointInstancedMesh,
    true
  );
  this.scene.add(midpointInstancedMesh);
  area.midpointInstancedMesh = midpointInstancedMesh;

  this.updateClosedLinePosition({
    line: area.combinedLine,
    points: area.points,
    forceUpdate: true,
  });

  this.draggedAreaPoint.index = pointIndex + 1;
};

export const updatePositionOnObject = function (object, position) {
  object.position.x = position.x;
  object.position.y = position.y;
  object.position.z = position.z;
};

export const createLineGroup = function (
  firstPoint,
  secondPoint,
  withLabel = true,
  withOffset = false,
  selected = false,
  skipLine = false
) {
  const solidLinePoints = [firstPoint.position, secondPoint.position];
  let solidLine;

  if (!skipLine) {
    solidLine = this.createReactiveThickLine(
      solidLinePoints,
      4.0,
      false,
      false,
      withLabel
        ? viewerColors.MIDPOINT_COLOR
        : selected
        ? viewerColors.SELECTED_LINE_COLOR
        : viewerColors.SOLAR_POINT_COLOR
    );
    this.scene.add(solidLine);
  }

  const midPoint = this.createReactiveMidPoint(
    firstPoint,
    secondPoint,
    withLabel
      ? viewerColors.MIDPOINT_COLOR
      : selected
      ? viewerColors.SELECTED_LINE_COLOR
      : viewerColors.SOLAR_POINT_COLOR
  );

  let label = undefined;
  if (withLabel) {
    label = this.createLabelBetweenTwoPoints(
      firstPoint.position,
      secondPoint.position,
      0.5,
      false,
      null,
      withOffset
    );
    this.scene.add(label);
  }

  return {
    line: solidLine,
    firstPoint: firstPoint,
    secondPoint: secondPoint,
    label: label,
    midPoint: midPoint,
  };
};

export const reDrawMeasurementAreaFromPoint = function (mergedPoints = false) {
  let currentMeasurementArea = this.draggedAreaPoint.area;
  this.selectedMeasurementArea = currentMeasurementArea.plane;
  const pointIndex = this.draggedAreaPoint.index;

  if (!currentMeasurementArea) return;

  currentMeasurementArea.complex = this.checkForComplexArea(
    currentMeasurementArea
  );

  const vectorPoints = currentMeasurementArea.points.map(
    (point) =>
      new THREE.Vector3(point.position.x, point.position.y, point.position.z)
  );

  const pointsAsArray = vectorPoints.map((point) => [
    point.x,
    point.y,
    point.z,
  ]);

  const flatPoints = [].concat(...pointsAsArray);

  const geometry = currentMeasurementArea.plane.geometry;
  const vertices = new Float32Array(flatPoints);

  let indices;

  if (currentMeasurementArea.complex) {
    indices = this.generateIndices(vectorPoints);
  } else {
    const triangleIndices = this.getTriangleIndices(
      currentMeasurementArea.points,
      this.getAxisDifferences(
        currentMeasurementArea.points.map((point) => point.position)
      )
    );
    indices = triangleIndices
      ? [].concat(...triangleIndices)
      : this.generateIndices(vectorPoints);
  }

  geometry.setAttribute("position", new THREE.BufferAttribute(vertices, 3));
  geometry.setIndex(new THREE.Uint16BufferAttribute(indices, 1));

  geometry.computeBoundingBox();

  this.updateClosedLinePosition({
    line: currentMeasurementArea.combinedLine,
    points: currentMeasurementArea.points,
    forceUpdate: true,
  });

  if (!mergedPoints) {
    const firstLineIndex =
      pointIndex - 1 < 0
        ? currentMeasurementArea.lines.length - 1
        : pointIndex - 1;
    const secondLineIndex = pointIndex;
    const newPoint = currentMeasurementArea.points[pointIndex];

    currentMeasurementArea.lines[firstLineIndex].secondPoint = newPoint;
    currentMeasurementArea.lines[secondLineIndex].firstPoint = newPoint;

    const linesToUpdate = [
      {
        line: currentMeasurementArea.lines[firstLineIndex],
        index: firstLineIndex,
      },
      {
        line: currentMeasurementArea.lines[secondLineIndex],
        index: secondLineIndex,
      },
    ];

    for (let lineToUpdate of linesToUpdate) {
      const line = lineToUpdate.line;
      const index = lineToUpdate.index;
      const label = line.label;
      this.updateLabelBetweenTwoPoints(
        label,
        line.firstPoint.position,
        line.secondPoint.position,
        0.5,
        true
      );

      this.updateMidPointPosition(
        currentMeasurementArea.midpointInstancedMesh,
        index,
        line.firstPoint.position,
        line.secondPoint.position
      );
    }
  }

  // recalculate area measurments
  let surfaceArea = "";
  let weightedAngle = "";

  if (!currentMeasurementArea.complex) {
    ({ surfaceArea, weightedAngle } = this.calculateAreaMeasurement(
      currentMeasurementArea.points
    ));
  }
  currentMeasurementArea.surfaceArea = surfaceArea;
  currentMeasurementArea.angle = weightedAngle;

  currentMeasurementArea.surfaceArea = surfaceArea;
  currentMeasurementArea.angle = weightedAngle;

  const label = currentMeasurementArea.label;
  const centerPoint = this.getCenterPointFromVectors(vectorPoints);
  label.position.set(centerPoint.x, centerPoint.y, centerPoint.z);
  const firstColumn = label.element.children[0];
  const [areaParagraph, angleParagraph] = firstColumn.children;
  areaParagraph.textContent = `${this.$n(Number(surfaceArea), "decimal")}m²`;
  angleParagraph.textContent = `${this.$n(Number(weightedAngle), "decimal")}°`;

  return currentMeasurementArea;
};

export const dragMeasurementAreaPointEnd = function () {
  const { area } = this.draggedAreaPoint;

  document.removeEventListener("mousemove", this.dragMeasurementAreaPoint);
  document.removeEventListener("mouseup", this.dragMeasurementAreaPointEnd);
  this.unholdMeasurementArea();
  if (this.active !== 4) this.enableDragControls();

  this.updateMeasurementAreaObject(
    area.id,
    area.points.map((point) => {
      return {
        x: point.position.x,
        y: point.position.y,
        z: point.position.z,
      };
    })
  );

  this.dragOn = false;
};

export const createMeasurementAreaObject = async function (points) {
  if (this.sample || this.anonymousUser) return {};
  const areaObject = {
    projectId: Number(this.projectId),
    position: points,
  };
  return await API.airteam3DViewer.createMeasurementAreaObject(areaObject);
};

export const updateMeasurementAreaObject = async function (id, points) {
  if (this.sample || this.anonymousUser) return;
  const areaObject = {
    id,
    projectId: Number(this.projectId),
    position: points,
  };
  return await API.airteam3DViewer.updateMeasurementAreaObject(areaObject);
};

export const deleteMeasurementAreaObject = async function (id) {
  if (this.sample || this.anonymousUser) return;
  return await API.airteam3DViewer.deleteObject(id);
};

export const removeUnfinishedAreaMeasurements = function () {
  const currentAreaMeasurement =
    this.measurementAreas[this.measurementAreas.length - 1];
  if (!currentAreaMeasurement || currentAreaMeasurement.closed) return;
  if (this.areaEndingLine) {
    this.removeObjectFromScene(this.areaEndingLine);
    this.areaEndingLine = null;
  }
  this.removeDashedLine();
  const tempLabel = this.scene.getObjectById(
    currentAreaMeasurement.tempLabel.id
  );
  this.scene.remove(tempLabel);

  this.removeSnapIcon(true);
  if (currentAreaMeasurement.points)
    this.removeArrayFromScene(currentAreaMeasurement.points);

  if (currentAreaMeasurement.lines) {
    this.removeArrayFromScene(
      currentAreaMeasurement.lines.map((line) => line.label)
    );
    this.removeArrayFromScene(
      currentAreaMeasurement.lines.map((line) => line.line)
    );
    currentAreaMeasurement.lines
      .map((line) => line.midPoint)
      .forEach((point) => {
        this.removeObjectWithChildrenFromScene(point);
      });
  }

  this.measurementAreas.pop();
};

export const removeLastMeasurementPoint = function (event) {
  const key = event.key;
  if (key !== "Backspace" && key !== "Delete") return;

  if (this.measurementAreas.length === 0) return;
  const currentArea = this.measurementAreas[this.measurementAreas.length - 1];
  if (currentArea.points.length === 0 || currentArea.closed) return;
  if (currentArea.points.length === 1) {
    this.removeUnfinishedAreaMeasurements();
  } else {
    this.undo();
  }
};

export const verifyMeasurementAreaLabelsVisibility = function (
  measurementArea
) {
  for (let i = 0; i < measurementArea.lines.length; i++) {
    const { label, firstPoint, secondPoint } = measurementArea.lines[i];

    const firstPointPosition = new THREE.Vector3();
    const secondPointPosition = new THREE.Vector3();
    firstPoint.getWorldPosition(firstPointPosition);
    secondPoint.getWorldPosition(secondPointPosition);

    if (!label.addedToScene) {
      const distance = this.updateLabelBetweenTwoPoints(
        label,
        firstPointPosition,
        secondPointPosition,
        0.5,
        true
      );

      if (distance > 0.5) this.scene.add(toRaw(label));
    }
  }
};
